'use client';

import { clsx } from 'clsx';
import { useLocale } from 'next-intl';
import { Image } from 'react-datocms';
import { Heading } from '~/components/Heading';
import { Box } from '~/components/Layout/Box';
import { Stack } from '~/components/Layout/Stack';
import { NewsletterForm } from '~/components/marketing/NewsletterForm';
import { Section } from '~/components/marketing/Section';
import type { Locale } from '~/i18n/i18n-config';
import { api } from '~/libs/api';
import { photoGradeClassName } from '~/utils/photoGrade';
import styles from './NewsletterBanner.module.css';
import type { NewsletterBannerProps } from './NewsletterBanner.types';

const NewsletterBanner = ({ data, slug }: NewsletterBannerProps) => {
  const { img, heading, description } = data;
  const locale = useLocale() as Locale;
  const newsletterSignup = api.automation.newsletterSignup.useMutation();
  const onSubscribe = (email: string, sourcePage: string) =>
    newsletterSignup.mutateAsync({ emailAddress: email, sourcePage, locale });

  return (
    <Section data-test-id="NewsletterBanner">
      <div className={styles.container}>
        {img?.responsiveImage && (
          <Image
            key={img.responsiveImage.src}
            data={img.responsiveImage}
            objectFit="cover"
            className={clsx(styles.image, photoGradeClassName(img.tags))}
          />
        )}
        <Box mdSpaceRight="--by-space-1200">
          <Stack space="--by-space-400">
            <Heading level="h3" variant="heading-md">
              {heading}
            </Heading>
            <p>{description}</p>
            <NewsletterForm onSubscribe={onSubscribe} source={slug} />
          </Stack>
        </Box>
      </div>
    </Section>
  );
};

export { NewsletterBanner };
